<script>
  import Interests from "../components/profile/Interests.svelte";
  import ProfileImage from "../components/profile/ProfileImage.svelte";

  export let theme;

  const socialLinks = [
    {
      icon: "fab fa-github",
      url: "https://github.com/manukj/",
      title: "GitHub",
    },
    {
      icon: "fab fa-linkedin-in",
      url: "https://www.linkedin.com/in/manu-kenchappa-junjanna-910455143/",
      title: "LinkedIn",
    },
    {
      icon: "fab fa-medium",
      url: "https://medium.com/@manu1998kj",
      title: "Medium",
    },
    {
      icon: "fab fa-twitter",
      url: "https://x.com/manu_k_j",
      title: "X (Twitter)",
    },
    {
      icon: "far fa-envelope",
      url: "mailto:manu1998kj@gmail.com",
      title: "Email",
    },
  ];
</script>

<section id="home" class="relative pt-20 overflow-hidden">
  <!-- Main Content -->
  <div
    class="relative max-w-7xl mx-auto px-4 min-h-[calc(100vh-5rem)] py-12 flex flex-col justify-center"
  >
    <div
      class="w-full grid grid-cols-1 lg:grid-cols-[1.3fr,0.7fr] gap-12 items-center"
    >
      <!-- Left Column -->
      <div class="space-y-8 transform-gpu">
        <!-- Enhanced Animated Badge -->
        <div
          class="inline-flex items-center px-4 py-2 space-x-3 bg-gradient-to-r from-violet-600/5 to-blue-600/5 dark:from-violet-600/10 dark:to-blue-600/10 rounded-full
          border border-gray-200/50 dark:border-white/10 hover:scale-105 hover:border-gray-300/50 dark:hover:border-white/20 transition-all duration-300 cursor-pointer group"
        >
          <span class="relative flex h-3 w-3">
            <span
              class="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"
            ></span>
            <span
              class="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"
            ></span>
          </span>
          <span
            class="text-sm font-medium text-gray-700 dark:text-white/80 group-hover:text-gray-900 dark:group-hover:text-white transition-colors duration-300"
            >Available for work</span
          >
        </div>

        <div>
          <h1 class="text-6xl sm:text-7xl font-bold tracking-tight mb-2">
            <div class="overflow-hidden">
              <span
                class="block text-gray-900 dark:text-white opacity-90 transform animate-title-slide-up"
              >
                I'm Manu
              </span>
            </div>
          </h1>

          <a
            href="https://fitlogai.co.uk"
            target="_blank"
            rel="noopener noreferrer"
            class="group inline-flex w-full sm:w-auto items-center gap-3 px-4 py-3 mb-5 rounded-2xl bg-gradient-to-r from-purple-700 via-violet-600 to-fuchsia-600 text-white border border-white shadow-[0_16px_34px_-12px_rgba(124,58,237,0.95)] transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_20px_40px_-12px_rgba(124,58,237,1)] focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-300/90"
            title="Open FitLog page in a new tab"
            aria-label="Founder of FitLog, open FitLog page in a new tab"
          >
            <span class="font-bold text-base md:text-lg tracking-tight">
              Founder of FitLog Ai
            </span>
            <span
              class="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-white/95"
            >
              Visit
              <i
                class="fas fa-external-link-alt text-xs transition-transform duration-200 group-hover:translate-x-0.5"
                aria-hidden="true"
              ></i>
            </span>
          </a>

          <p class="text-2xl text-gray-900 dark:text-white/60 opacity-90 mb-4">
            Full-stack Developer with a Passion for Building Impactful Products
          </p>

          <p
            class="text-xl text-gray-600 dark:text-white/60 max-w-2xl leading-relaxed font-light tracking-wide mb-6"
          >
            With Msc in Advanced Computer Science and over 5 years of
            experience, I specialize in developing robust, user-focused
            applications across Android/IOS and web platforms.
          </p>

          <p
            class="text-lg pt-4 text-gray-500 dark:text-white/40 max-w-2xl leading-relaxed font-light"
          >
            Currently learning about blockchain, cryptography, and quantum
            computing. Always curious about new technologies and how they can
            solve real problems.
          </p>
        </div>

        <!-- Interests -->
        <div class="pt-4">
          <Interests />
        </div>
      </div>

      <!-- Enhanced Right Column - Profile Image -->
      <div
        class="relative group perspective-1000 sticky top-32 max-w-md mx-auto lg:max-w-sm"
      >
        <div
          class="absolute inset-0 bg-gradient-to-r from-violet-600/10 via-blue-600/10 to-cyan-600/10 dark:from-violet-600/20 dark:via-blue-600/20 dark:to-cyan-600/20
          rounded-3xl blur-2xl group-hover:blur-3xl transition-all duration-500 opacity-0 group-hover:opacity-100"
        ></div>
        <div
          class="relative transform group-hover:scale-[1.02] group-hover:-rotate-2 transition-all duration-500
          hover:shadow-[0_0_3rem_-1rem_theme(colors.violet.500)]"
        >
          <ProfileImage />
        </div>

        <!-- Social Links and CTA -->
        <div
          class="absolute -bottom-24 left-1/2 -translate-x-1/2 flex flex-col items-center gap-4"
        >
          <!-- Social Links -->
          <div
            class="flex items-center gap-4 p-3 bg-white/10 dark:bg-white/5 backdrop-blur-lg rounded-2xl border border-gray-200/20 dark:border-white/10"
          >
            {#each socialLinks as { icon, url, title }}
              <a
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                class="p-2 hover:bg-white/10 rounded-xl transition-all duration-300 hover:scale-110 hover:-translate-y-1"
                {title}
              >
                <i class="{icon} text-gray-600 dark:text-white/70"></i>
              </a>
            {/each}
          </div>

          <!-- CTA Button -->
          <a
            href="#contact"
            class="group relative px-8 py-3 overflow-hidden bg-gradient-to-r from-gray-100/80 to-gray-50/80 dark:from-violet-600/10 dark:to-blue-600/10
            backdrop-blur-xl rounded-lg border border-gray-200/50 dark:border-white/10 hover:border-gray-300/50 dark:hover:border-white/20
            transition-all duration-500 hover:shadow-[0_0_2rem_-0.5rem_theme(colors.blue.500)]"
          >
            <div
              class="absolute inset-0 bg-gradient-to-r from-violet-600/10 to-blue-600/10 dark:from-violet-600/20 dark:to-blue-600/20 opacity-0
              group-hover:opacity-100 transition-opacity duration-500 blur-xl"
            ></div>
            <span
              class="relative flex items-center gap-3 text-gray-700 dark:text-white/90 font-medium"
            >
              Let's talk
              <svg
                class="w-5 h-5 transform transition-all duration-500 group-hover:translate-x-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M17 8l4 4m0 0l-4 4m4-4H3"
                />
              </svg>
            </span>
          </a>
        </div>
      </div>
    </div>
  </div>
</section>

<style>
  @keyframes title-slide-up {
    from {
      transform: translateY(100%);
    }
    to {
      transform: translateY(0);
    }
  }

  .animate-title-slide-up {
    animation: title-slide-up 1s cubic-bezier(0.22, 1, 0.36, 1);
  }

  .animation-delay-150 {
    animation-delay: 150ms;
  }

  .animate-fade-in {
    animation: fade-in 1s cubic-bezier(0.22, 1, 0.36, 1);
  }

  @keyframes fade-in {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  .perspective-1000 {
    perspective: 1000px;
  }

  @keyframes tilt {
    0%,
    100% {
      transform: rotate(0deg);
    }
    25% {
      transform: rotate(1deg);
    }
    75% {
      transform: rotate(-1deg);
    }
  }

  .animate-tilt {
    animation: tilt 10s ease-in-out infinite;
  }

  .delay-75 {
    animation-delay: 75ms;
  }

  .delay-150 {
    animation-delay: 150ms;
  }
</style>
